<template>
    <div class="o-map-exploration-views" >
        <div>
            <BaseView
                v-if="mapViewer.activeView === 'ShowreelView'"
                name="ShowreelView"
                key="showreelView"

                @coverClick_="goToFloorSelectionView"
                @unitClick_="goToOutdoorView"
                @floorClick="goToOutdoorView"

                :highlight-covers-on-mouse-over-OFF="true"

                :camera-orientation="activeScene.orientation"
                :camera-tilt="activeScene.tilt"

                :zoom="activeScene.zoom"

                :limit-max-zoom-to="9000"
                :max-zoom-margin="0.5"

                :show-covers="shouldShowCoversOnOutdoorView"
                :show-outdoor="showOutdoor"
                :show-floors="visibleFloorsUnitNames"

                :show-icons="outdoorViewIcons"

                :highlight-units="unitsHighlightColors"
                :highlight-floor-walls="floorsWallsHighlightColors"

                :transparent-covers="this.emptyArray"
                :transition-duration="activeSceneDuration"
                @transitionEnd="nextScene"

                :center-on-units="activeScene.center"
            >
                <MapLabel
                    v-for="building in anchorBuildings"
                    :key="building.id"
                    :selectedUnit="building.floor.unit_name"
                    target="floor"
                >
                    <ADialog
                        icon="marker"
                        :fill="getOutdoorViewConfig('labels.buildings.marker.color','')"
                        :cursorPointer="getOutdoorViewConfig('events.cover_click', false)"
                        @click="goToFloorSelectionView(building.meta.building_index)"
                    >
                        {{ t(building, 'name') }}
                    </ADialog>
                </MapLabel>
            </BaseView>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

// import AButton from "../../atoms/a-button/a-button";
import MapLabel from "../../../shared/components/map-viewer/labels/map-label"
import BaseView from "../../../shared/components/map-viewer/views/base/base-view";
import OutdoorView from "../../../shared/components/map-viewer/views/outdoor/outdoor-view"

import {
    REMOVE_PATH,
    MAP_SHOW_FLOOR,
    MAP_SHOW_OBJECT,
    SET_NORMALIZED_ZOOM,
    SET_GYROSCOPE_STATE,
} from "@/shared/components/map-viewer/constants/map-commands";
import {
    MAP_OBJECT_HOVERED,
    MAP_OBJECT_SELECTED
} from "@/shared/components/map-viewer/constants/map-events";
import ADialog from "@/components/atoms/a-dialog/a-dialog";
import {arrayValues2ObjectKeys, objects2UnitColors} from "@/utils/helpers/common";

export default {
    name: "OMapShowreelViews",
    components: {
        ADialog,
        BaseView,
        MapLabel,
    },

    // -------------------------------- Injected ----------------------------------------
    inject: ['mapViewer'],

    // -------------------------------- Data --------------------------------------------
    data(){
        return {
            activeBuildingId: null,
            activeLevel: null,
            activeFloorUnitName: null,
            activeUnitName: null,

            objectsInFocus: [],
            floorsInFocus: [],
            selectedObjects: [],

            startScene: true,
            activeSceneIndex: 0,
            defaultScene:{
                "center": [],
                "zoom": 3000,
                "tilt": 15,
                "orientation": 0,
                "duration": 0
            },
        }
    },

    // -------------------------------- Computed ----------------------------------------
    computed: {
        ...mapGetters([
            't',
            'getMap',
            'getMapConfig',
            'getMapConfigProp',
        ]),

        scenes(){
            return _.get( this.mapViewer.mapData.config, 'map_views.showreel.scenes', [])
        },
        activeScene(){
            return _.merge(this.defaultScene, this.scenes[this.activeSceneIndex]);
        },
        activeSceneDuration(){
            return this.startScene ? 0 : this.activeScene.duration;
        },

        emptyArray(){
            return []
        },
        md(){
            return this.mapViewer.mapData
        },
        activeFloor(){
            return this.floors.find( f => f.meta.building_index === this.activeBuildingId && f.meta.level === this.activeLevel)
        },
        activeFloorUnitNames(){
            return this.activeFloor ? [this.activeFloor.unit.name] : this.emptyArray
        },
        activeFloorPortals(){
            // TODO: move allowed portal types to a guide settings
            return this.portals.filter( p => p.floor.unit_name === this.activeFloorUnitName && p.meta.portal_type === 'Elevators')
        },

        visibleFloorsUnitNames(){
            const showGroundFloors = this.getOutdoorViewConfig('floors.ground',false);
            return [
                ...this.outdoorFloorsUnitNames,
                //...this.selectedObjectsFloorsUnitNames,
                ...(showGroundFloors ? this.groundFloorsUnitNames : []),
            ]
        },

        groundFloors(){
            return this.floors.filter( f => f.meta.is_ground )
        },
        groundFloorsIds(){
            return this.groundFloors.map( f => f.id );
        },
        filtredGroundFloorObjects(){
            return this.groundFloorsObjects.filter( o => !o.meta.is_visible_with_covers);
        },
        groundFloorsObjects(){
            return this.objects.filter( o => this.groundFloorsIds.includes(o.floor.id) );
        },
        groundFloorsUnitNames(){
            return this.groundFloors.map( f => f.unit.name );
        },

        outdoorBuildingsIDs(){
            return this.buildings.filter( b => b.meta.is_outdoor)
        },

        outdoorFloors(){
            return this.floors.filter( f => f.meta.is_outdoor)
        },
        outdoorFloorsUnitNames(){
            return this.outdoorFloors.map( f => f.unit.name)
        },
        outdoorFloorsIds(){
            return this.outdoorFloors.map( f => f.id )
        },
        outdoorFloorObjects(){
            return this.objects.filter( o => this.outdoorFloorsIds.includes(o.floor.id) );
        },
        filtredOutdoorFloorObjects(){
            return this.outdoorFloorObjects.filter( o => !o.meta.is_visible_with_covers);
        },
        outdoorViewIcons(){
            const outdoorFloorsIcons = this.filtredOutdoorFloorObjects.reduce( (acc, obj) => {
                switch (obj.type){
                    case 'portal':
                    case 'label':
                        acc.push(`${obj.id}--${obj.unit.name}`);
                        break;
                    case 'tenant':
                    case 'service':
                        acc.push(`${obj.id}--${obj.unit.name}`);
                        break;
                }
                return acc;
            }, []);

            const groundFloorsIcons = this.filtredGroundFloorObjects.reduce( (acc, obj) => {
                switch (obj.type){
                    case 'label':
                        acc.push(`${obj.id}--${obj.unit.name}`);
                        break;
                    case 'tenant':
                    case 'service':
                        !this.shouldShowCoversOnOutdoorView &&
                            acc.push(`${obj.id}--${obj.unit.name}`);
                        break;
                }
                return acc;
            }, []);

            return [...outdoorFloorsIcons, ...groundFloorsIcons]
        },

        shouldShowCoversOnOutdoorView(){
            return this.getOutdoorViewConfig('covers', true);
        },

        tenants(){
            return this.objects.filter( o => o.type === 'tenant');
        },
        services(){
            return this.objects.filter( o => o.type === 'service');
        },
        anchorTenants(){
            return this.tenants.filter( o => o.meta.anchor );
        },

        anchorServices(){
            return this.services.filter( o => o.meta.anchor );
        },
        anchorTenantUnitNames(){
            return this.anchorTenants.map( obj => obj.unit.name );
        },
        anchorServicesUnitNames(){
            return this.anchorServices.map( obj => obj.unit.name );
        },

        units(){
            return this.mapViewer.mapData.units;
        },
        unitNames(){
            return this.units.map( u => u.name);
        },
        modelUnits(){
            return this.units.filter( u => u.type === 'dae_unit')
        },
        modelUnitsNames(){
            return this.modelUnits.map( u => u.name);
        },

        indoorFloors(){
            return this.floors.filter( f => !f.meta.is_outdoor );
        },
        indoorFloorsUnitNames(){
            return this.indoorFloors.map( f => f.unit.name )
        },

        floorsWallsHighlightColors(){
            const color =  this.getMapConfigProp(`floors.default.walls_color`);
            return color ? arrayValues2ObjectKeys(this.indoorFloorsUnitNames, color) : {};
        },

        levels(){
            return Array.from(this.floors.reduce((acc,f) => acc.add(f.meta.level), new Set()));
        },
        floors(){
            return this.mapViewer.mapData.objects.filter( o => o.type === 'floor');
        },
        portals(){
            return this.mapViewer.mapData.objects.filter( o => o.type === 'portal');
        },
        objects(){
            return this.mapViewer.mapData.objects;
        },
        entrances(){
            return this.portals.filter( p => p.meta.is_entrance )
        },

        buildings(){
            return this.mapViewer.mapData.objects.filter( o => o.type === 'building');
        },
        anchorBuildings(){
            return this.buildings.filter( o => o.meta.anchor);
        },

        buildingIds(){
            return this.buildings.map( b => b.meta.building_index)
        },
        showOutdoor(){
            return this.activeLevel === null || this.getMapConfigProp('global_map.outdoor_levels', [0]).includes(this.activeLevel)
        },
        isDebug(){
            return document.location.href.includes('localhost');
        },

        unitsHighlightColors(){
            return _.merge(
                {},
                this.unitsColors,
                this.objectUnitColors,
                this.anchorTenantUnitsColors,
                this.anchorServicesUnitsColors,
                this.selectedObjectsUnitsColor
            )
        },
        unitsColors(){
            const color = this.getMapConfigProp(`map_views.default.units.color`);
            return color ? arrayValues2ObjectKeys(this.modelUnitsNames, color) : {};
        },
        selectedObjectsUnitsColor(){
            const selectedColor = this.getMapConfigProp(`map_views.default.units.selected_color`);
            return selectedColor ? objects2UnitColors(this.selectedObjects, 'selected_color', selectedColor) : {};
        },
        objectUnitColors(){
            const color = this.getMapConfigProp(`map_views.default.units.color`);
            return color ? objects2UnitColors(this.selectedObjects, 'color', color) : {};
        },

        anchorTenantUnitsColors(){
            const color = this.getMapConfigProp(`map_views.default.units.anchor_tenant_color`) ||
                this.getMapConfigProp(`map_views.default.units.anchor_color`);
            return objects2UnitColors(this.anchorTenants, 'anchor_color', color);
        },
        anchorServicesUnitsColors(){
            const color = this.getMapConfigProp(`map_views.default.units.anchor_service_color`) ||
                this.getMapConfigProp(`map_views.default.units.anchor_color`);
            return objects2UnitColors(this.anchorServices, 'anchor_color', color);
        },
    },

    // -------------------------------- Methods ---------------------------------------
    methods: {
        nextScene(){
            if(this.activeSceneIndex + 1 < this.scenes.length)
            {
                this.activeSceneIndex++;
            }
            else
            {
                this.activeSceneIndex = 0;
            }

            if(this.startScene)
            {
                this.startScene = false;
            }

            this.mapViewer.$data.isFirstView = false;
        },

        onFloorClick(floorUnit){
            const f = this.floors.find(f => f.unit.name === floorUnit)
            this.activeUnitName = null
            this.activeLevel = f.meta.level
            this.activeBuildingId = f.meta.building_index
            this.activeFloorUnitName = floorUnit
            this.$emit('changeView','LevelView')
        },

        goToOutdoorView(){
            this.$emit('changeView','OutdoorView')
        },

        goToFloorSelectionView(buildingId, level = 0){
            /*    this.$router.push({
                query:{
                    mv: 'FloorSelection',
                    mvb: buildingId,
                }
            });*/
            this.$emit('changeView','OutdoorView');
            //this.$emit('changeView','FloorSelection');
        },
        goToLevelViewByObject(obj){
            this.resetSelected()

            this.activeBuildingId = obj.floor.building_index
            this.activeLevel = obj.floor.level
            this.objectsInFocus = [obj]

            this.$emit('changeView','LevelView')
        },
        showFloor({id, unit}){
            const floor = this.findFloorById(id)
            floor && this.goToLevelView(floor.meta.building_index, floor.meta.level)
        },
        showObject({id, unit}){
            const o = this.findObject(id, unit);
            o && this.goToLevelViewByObject(o)
        },
        findFloorByBuildingAndLevel(buildingId, level){},
        findFloorById(id){
            return this.floors.find( f => f.id === id)
        },
        findObject(id, unit = null){
            return this.mapViewer.mapData.objects.find( f => f.id === id && !unit || f.unit.name === unit )
        },
        selectUnitObjects(unitName){
            this.objectsInFocus = this.selectedObjects = unitName ? this.hoverableObjects.filter( o => o.unit.name === unitName ) : this.emptyArray
        },
        selectedObjectsByUnitName(unitName){
            return this.selectedObjects.filter( o => o.unit.name === unitName)
        },
        getOutdoorViewConfig(prop, def){
            return this.getMapConfigProp(`map_views.showreel.${prop}`) || this.getMapConfigProp(`map_views.default.${prop}`, def);
        },
    },

    // -------------------------------- Hooks ---------------------------------------
}
</script>
<style lang="scss">
  @import "o-map-showreel-views";
</style>

